import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  /** Der Begriff, den es erklärt — steht im Vorlese-Namen des Knopfes und über der Erklärung. */
  label: string;
  /** Die Erklärung selbst, ein bis zwei Sätze. */
  text: string;
  /**
   * Was auf dem Knopf steht. Ohne Angabe das Fragezeichen hinter einem Kennzahlen-Namen; der
   * Briefkopf setzt hier sein Schloss bzw. seine Weltkugel ein — dort **ist** das Zeichen die
   * Auskunft, und ein Fragezeichen daneben wäre ein zweites Ziel für dieselbe Frage.
   */
  children?: React.ReactNode;
  /** Klasse des Knopfes, wenn er nicht wie ein Fragezeichen aussehen soll. */
  triggerClassName?: string;
}

/**
 * Ein dezentes Fragezeichen hinter einem Kennzahlen-Namen, das die Erklärung aufklappt.
 *
 * **Warum `position: fixed`.** Die Tabelle steht in einem `overflow-x:auto`-Container, und der
 * schneidet nach CSS auch senkrecht ab: ein absolut positioniertes Feld am Spaltenkopf wäre halb
 * abgeschnitten oder erzeugte eine Scrollleiste. Ein `fixed` positioniertes Element hängt dagegen
 * am Sichtfenster und ignoriert den Container — solange kein Vorfahre `transform`, `filter` oder
 * `will-change` setzt (das machte ihn zum Bezugsrahmen). Hier ist das keiner; die einzige
 * Animation im Bereich (`vb-row`) liegt in der Terminliste.
 *
 * **Warum Klick und nicht Hover.** Ein `title`-Attribut hat keine Anfassmarke — niemand fährt
 * über einen Tabellenkopf, um zu prüfen, ob dort etwas passiert — und auf dem Handy gibt es es
 * gar nicht. Das Fragezeichen ist sichtbar, ein echter Knopf und damit auch mit der Tastatur
 * erreichbar.
 *
 * Beim Scrollen schließt es sich: ein am Sichtfenster hängendes Feld würde sonst neben seinem
 * Begriff wegwandern und über fremden Zahlen stehenbleiben.
 *
 * **Gerendert wird es an `document.body`** (Portal). Im Tabellenkopf stand es sonst als Kind eines
 * `th`, und das vererbt Schrift, Versalien, Laufweite und Farbe der Spaltenüberschrift: die
 * Erklärung erschien in rosa Versalien-Mono und war praktisch unlesbar. Jede vererbte Eigenschaft
 * einzeln zurückzusetzen wäre eine Liste, die beim nächsten neuen Ort wieder unvollständig ist.
 */
const MetricHint: React.FC<Props> = ({ label, text, children, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useRef(`hint-${Math.random().toString(36).slice(2, 9)}`);

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const width = 260;
    // In den Sichtbereich geklemmt: an der äußersten Spalte stünde das Feld sonst halb draußen.
    const left = Math.min(Math.max(8, r.left + r.width / 2 - width / 2), window.innerWidth - width - 8);
    setPos({ top: r.bottom + 8, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    // `capture`, weil das Scrollen im Tabellen-Container passiert und nicht am Fenster.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={triggerClassName ?? 'vb-hint-btn'}
        aria-label={`Was bedeutet „${label}"?`}
        aria-expanded={open}
        aria-describedby={open ? id.current : undefined}
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
      >
        {children ?? '?'}
      </button>
      {open && pos && createPortal(
        <span
          id={id.current}
          role="tooltip"
          className="vb-hint-pop"
          style={{ top: pos.top, left: pos.left }}
          onClick={e => e.stopPropagation()}
        >
          <strong>{label}</strong>
          {text}
        </span>,
        document.body,
      )}
    </>
  );
};

export default MetricHint;
